import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff, Mail, Lock, ArrowRight } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import api from "@/services/api";
import { LanguageSelector } from "../LanguageSelector";
import { useTranslation } from "react-i18next";
import './LoginScreen.css'; // Added this line
import { PRIVACY_VERSION, TERMS_VERSION } from '../../config/legalVersions';

const GoogleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-5 h-5 drop-shadow-[0_0_10px_rgba(255,255,255,0.4)]"
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M21.35 11.1H12v2.96h5.27c-.23 1.3-.96 2.32-2.05 2.98l3.29 2.55c1.92-1.77 3.04-4.38 3.04-7.5 0-.64-.06-1.26-.2-1.99z"
    />
    <path
      fill="#34A853"
      d="M12.18 22c2.73 0 5.03-.92 6.71-2.49l-3.29-2.55c-.91.62-2.07.99-3.42.99-2.63 0-4.86-1.78-5.67-4.18H3.95v2.6C5.62 19.98 8.64 22 12.18 22z"
    />
    <path
      fill="#FBBC05"
      d="M6.51 13.77a6.3 6.3 0 0 1 0-3.54V7.63H3.95a9.83 9.83 0 0 0 0 8.74l2.56-2.6z"
    />
    <path
      fill="#EA4335"
      d="M12.18 6.86c1.48 0 2.81.49 3.86 1.45l2.88-2.88C17.23 3.54 14.92 2.6 12.18 2.6 8.64 2.6 5.62 4.62 3.95 7.63l2.56 2.6c.81-2.4 3.04-3.37 5.67-3.37z"
    />
  </svg>
);

const LoginScreen = ({ onSwitchToRegister, onClose, onForgotPassword }) => {
  const { t, i18n } = useTranslation();
  const { login, loginWithGoogle, error, loading, clearError, isNativeApp } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");
  const [isGoogleConsentChecked, setIsGoogleConsentChecked] = useState(false);
  const [captcha, setCaptcha] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (error) clearError();
    if (localError) setLocalError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!formData.email || !formData.password) {
      setLocalError(t('auth.error_required_fields') || "Please fill in all fields");
      return;
    }

    try {
      const result = await login(formData.email, formData.password);
      if (result?.success) {
        if (onClose) onClose();
      } else {
        setLocalError(result?.error || t('auth.error_login_failed') || "Invalid email or password");
      }
    } catch (err) {
      console.error("Login error:", err);
      setLocalError(t('auth.error_generic') || "Login failed. Please try again.");
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLocalError("");
      if (!isGoogleConsentChecked) {
        setLocalError(
          "You must agree to the Terms of Service and Privacy Policy to continue with Google."
        );
        return;
      }

      const result = await loginWithGoogle();
      if (!result?.success && result?.error) {
        setLocalError(result.error);
      }
    } catch (error) {
      console.error("Google login error:", error);
      setLocalError(t('auth.error_google_failed') || "Google login failed. Please try again.");
    }
  };

  return (
    <>
      {/* Removed <style>{glassCardStyles}</style> tag */}
      <div className="w-full min-h-screen bg-slate-950 flex items-start md:items-center justify-center p-4 pt-12 md:pt-4 overflow-hidden relative">
        {/* Subtle background gradients for depth */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 blur-[120px] rounded-full" />

        <div className="absolute top-4 left-4 z-50">
          <LanguageSelector
            variant="cube"
            className="w-auto"
            value={i18n.language}
            onChange={(newLang) => {
              localStorage.setItem('scrolled_preferred_language', newLang);
              i18n.changeLanguage(newLang);
            }}
          />
        </div>

        {/* Ceiling Rays Effect */}
        <div
          className="absolute top-0 left-0 w-full h-[300px] pointer-events-none ceiling-rays"
        />

        <div className="w-full max-w-md relative z-10">
          {/* Header - Apple Style */}
          <div className={`text-center ${isNativeApp ? 'mb-8 mt-12' : 'mb-6 md:mb-10'}`}>
            <div className="mb-6 md:mb-8 flex justify-center">
              <img
                src="/logo/scrolled-logo.png"
                alt="ScrollEd"
                className="w-[80%] max-w-[280px] drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)] brightness-110"
              />
            </div>
            <p className="text-white/60 text-xs font-semibold uppercase tracking-[0.2em]">
              {t('auth.sign_in_title')}
            </p>
          </div>

          {/* Login Form */}
          <div className="glass-modal-card p-6 md:p-10 space-y-6 md:space-y-8">
            {!isNativeApp && (
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                {/* Error Display */}
                {(error || localError) && (
                  <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-200 backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-300">
                    <p className="text-xs font-bold uppercase tracking-wider text-center">
                      {error || localError}
                    </p>
                  </div>
                )}

                {/* Email Field */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/60 ml-1">
                    {t('auth.email_label')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-30">
                      <Mail className="w-4 h-4 text-white" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-3.5 rounded-2xl premium-input text-white placeholder-white/50 focus:outline-none text-sm font-medium"
                      placeholder="email@example.com"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 ml-1">
                    {t('auth.password_label')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-30">
                      <Lock className="w-4 h-4 text-white" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-14 py-3.5 rounded-2xl premium-input text-white placeholder-white/50 focus:outline-none text-sm font-medium"
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-2 text-white/40 hover:text-white/80 transition-all"
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Login Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full h-14 overflow-hidden rounded-2xl bg-white text-black font-extrabold text-sm uppercase tracking-[0.15em] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 shadow-[0_10px_30px_rgba(255,255,255,0.1)] hover:shadow-[0_15px_40px_rgba(255,255,255,0.2)]"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
                        <span>{t('common.loading')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span>{t('auth.sign_in_button')}</span>
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                    )}
                  </button>
                </div>

                {/* Forgot Password Link */}
                {onForgotPassword && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={onForgotPassword}
                      className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/50 hover:text-white/80 transition-all"
                      disabled={loading}
                    >
                      {t('auth.forgot_password')}
                    </button>
                  </div>
                )}
              </form>
            )}

            {/* Divider */}
            {!isNativeApp && (
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-transparent text-[9px] font-extrabold uppercase tracking-[0.3em] text-white/40">
                    {t('auth.or_divider') || 'OR'}
                  </span>
                </div>
              </div>
            )}

            {/* Google Login Button */}
            <div className={isNativeApp ? 'py-4' : ''}>
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full h-14 rounded-2xl border border-white/10 bg-white/5 text-white font-bold text-sm hover:bg-white/10 transition-all duration-300 flex items-center justify-center space-x-3 group"
              >
                <div className="transition-transform duration-500 group-hover:scale-110">
                  <GoogleIcon />
                </div>
                <span>Continue with Google</span>
              </button>
            </div>

            {/* Explicit consent required before Google can create or access an account */}
            <div className="flex flex-row gap-3 items-start px-1">
              <input
                type="checkbox"
                name="google-legal-consent"
                id="google-legal-consent"
                checked={isGoogleConsentChecked}
                onChange={() => setIsGoogleConsentChecked(!isGoogleConsentChecked)}
                className="w-4 h-4 rounded-lg border-white/20 bg-white/5 text-white focus:ring-0 transition-all cursor-pointer"
              />
              <div className="text-[10px] font-bold text-white/60 uppercase tracking-wide leading-4 pt-0.5">
                <label htmlFor="google-legal-consent" className="cursor-pointer select-none">
                  By continuing with Google, I agree to the{' '}
                </label>
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-legal-version={TERMS_VERSION}
                  className="text-cyan-100 underline underline-offset-2 hover:text-white"
                >
                  Terms of Service
                </a>{' '}
                and acknowledge the{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-legal-version={PRIVACY_VERSION}
                  className="text-cyan-100 underline underline-offset-2 hover:text-white"
                >
                  Privacy Policy
                </a>.
              </div>
            </div>

            {/* Switch to Register */}
            {!isNativeApp && (
              <div className="text-center pt-2">
                <p className="text-xs font-medium text-white/60">
                  {t('auth.no_account')}{" "}
                  <button
                    onClick={onSwitchToRegister}
                    className="text-white hover:underline underline-offset-4 decoration-white/20 transition-all font-semibold"
                    disabled={loading}
                  >
                    {t('auth.sign_up_link')}
                  </button>
                </p>
              </div>
            )}

            {/* Legal Links */}
            <div className="text-center text-[9px] font-medium text-white/40 pt-6 border-t border-white/5 space-y-1">
              <p>
                {t('auth.agree_terms_pre')} {" "}
                <a href="/terms" className="text-white/60 hover:text-white transition-colors underline underline-offset-2 uppercase tracking-tighter">
                  {t('auth.terms_service_link')}
                </a>{" "}
                {t('auth.agree_terms_mid')} {" "}
                <a href="/privacy" className="text-white/60 hover:text-white transition-colors underline underline-offset-2 uppercase tracking-tighter">
                  {t('auth.privacy_policy_link')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;